import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Hero } from '@/components/landing/Hero';
import { ProblemSection } from '@/components/landing/ProblemSection';
import { SolutionInAction } from '@/components/landing/SolutionInAction';
import { HowItWorks } from '@/components/landing/HowItWorks';
import { Testimonials } from '@/components/landing/Testimonials';
import { Pricing } from '@/components/landing/Pricing';
import { FAQ } from '@/components/landing/FAQ';
import { CTA } from '@/components/landing/CTA';

export const HomePage: React.FC = () => {
  const analytics = useAnalytics();
  
  // Track specific home page events
  React.useEffect(() => {
    // Track hero section view
    const timer = setTimeout(() => {
      analytics.trackEvent({
        action: 'view',
        category: 'engagement',
        label: 'hero_section',
        custom_parameters: {
          page_section: 'hero'
        }
      });
    }, 3000); // After 3 seconds on page
    
    return () => clearTimeout(timer);
  }, [analytics]);
  
  return (
    <>
      <Helmet>
        <title>Passez de F/G à D sans gaspiller un euro | DPEWizard</title>
        <meta name="description" content="Notre IA analyse votre DPE officiel et vous montre exactement quels travaux faire pour gagner des lettres - en dépensant le minimum." />
        <meta name="keywords" content="erreurs DPE, récupérer argent vente immobilière, DPE incorrect, économiser vente maison, diagnostic énergétique faux, classe énergétique améliorée" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.dpewizard.com/" />
        <meta property="og:title" content="Passez de F/G à D sans gaspiller un euro" />
        <meta property="og:description" content="Notre IA analyse votre DPE officiel et vous montre exactement quels travaux faire pour gagner des lettres - en dépensant le minimum." />
        <meta property="og:image" content="https://www.dpewizard.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.dpewizard.com/" />
        <meta property="twitter:title" content="Passez de F/G à D sans gaspiller un euro" />
        <meta property="twitter:description" content="Notre IA analyse votre DPE officiel et vous montre exactement quels travaux faire pour gagner des lettres - en dépensant le minimum." />
        <meta property="twitter:image" content="https://www.dpewizard.com/twitter-image.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.dpewizard.com/" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DPEWizard",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": [
              {
                "@type": "Offer",
                "price": "9.90",
                "priceCurrency": "EUR",
                "name": "Analyse DPE Unique",
                "priceValidUntil": "2026-12-31"
              },
              {
                "@type": "Offer",
                "price": "19.90",
                "priceCurrency": "EUR",
                "name": "Abonnement Mensuel Illimité",
                "priceValidUntil": "2026-12-31"
              }
            ],
            "description": "Notre IA analyse votre DPE officiel et vous montre exactement quels travaux faire pour gagner des lettres - en dépensant le minimum.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "500"
            },
            "potentialAction": {
              "@type": "UseAction",
              "target": "https://www.dpewizard.com/auth",
              "object": "Analyse DPE"
            }
          })}
        </script>
        
        {/* FAQ Schema pour améliorer le SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Le ROI affiché est-il fiable ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, à condition de bien comprendre ce qu'il représente. Le ROI que nous affichons mesure exclusivement le gain en valeur immobilière rapporté au coût des travaux. Il ne prend pas en compte les économies d'énergie futures, les aides financières (CEE, MaPrimeRénov'), ni l'amélioration du confort thermique. C'est donc un ROI « plancher » : l'impact réel sur votre patrimoine et votre portefeuille sera supérieur une fois ces autres bénéfices intégrés."
                }
              },
              {
                "@type": "Question",
                "name": "Pourquoi DPEWizard affiche deux lettres DPE et pas une seule ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Parce que depuis juillet 2021, le DPE officiel se base sur deux critères distincts : la consommation d'énergie primaire (kWhEP/m²/an) ET les émissions de gaz à effet de serre (kgCO2/m²/an). Chacun génère sa propre lettre (A à G). C'est la plus mauvaise des deux qui détermine votre classe finale. DPEWizard vous montre les deux lettres séparément pour que vous compreniez précisément sur quel levier agir : si vous êtes D en énergie mais F en GES, c'est votre mode de chauffage (fioul, gaz) qu'il faut changer en priorité."
                }
              },
              {
                "@type": "Question",
                "name": "Les nouveaux coefficients 2026 sont-ils pris en compte ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, DPEWizard intègre les nouvelles pondérations introduites en janvier 2026, notamment la réévaluation de l'électricité (coefficient passé de 2,3 à 2,1) et le poids accru des émissions carbone. Cela signifie que nos projections tiennent compte de l'évolution réglementaire en cours : un chauffage électrique sera légèrement mieux noté qu'avant, tandis que les énergies fossiles (fioul, gaz) seront davantage pénalisées."
                }
              },
              {
                "@type": "Question",
                "name": "Mon DPE date de plusieurs années — les calculs restent-ils valables ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui et non. Si votre DPE est antérieur à juillet 2021, il utilise l'ancienne méthode (sur factures) qui n'est plus opposable depuis 2025. Dans ce cas, DPEWizard vous alertera et vous recommandera de faire refaire un DPE selon la méthode 3CL actuelle avant de planifier des travaux. Si votre DPE date d'après juillet 2021, nos calculs s'appuient sur les données techniques du bien (isolation, vitrages, chauffage) qui restent stables dans le temps : nos projections seront donc fiables même 2 ou 3 ans après l'établissement du diagnostic."
                }
              },
              {
                "@type": "Question",
                "name": "Puis-je comparer plusieurs stratégies de travaux ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, c'est justement l'usage principal de DPEWizard. Une fois votre DPE analysé, vous accédez au simulateur interactif où vous pouvez cocher/décocher n'importe quelle combinaison de travaux (isolation des murs, changement de chaudière, fenêtres, VMC…). Chaque modification recalcule instantanément votre DPE projeté et le ROI correspondant. Vous pouvez ainsi tester des dizaines de scénarios (« Et si je fais juste les murs + la VMC ? », « Et si j'ajoute le changement de chaudière ? ») pour identifier la stratégie la plus rentable selon votre budget."
                }
              },
              {
                "@type": "Question",
                "name": "Combien de temps faut-il pour obtenir une analyse complète ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "L'analyse automatique de votre DPE prend environ 10 secondes. Ensuite, selon la complexité de votre bien, vous répondrez à 5 à 20 questions personnalisées. Au total, comptez 2 à 5 minutes pour obtenir votre stratégie d'optimisation complète."
                }
              },
              {
                "@type": "Question",
                "name": "Que se passe-t-il si mon DPE ne contient aucune erreur ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Même avec un DPE correct, DPEWizard vous donne accès au simulateur de travaux pour tester l'impact de différentes améliorations énergétiques. Vous recevrez des recommandations personnalisées selon vos objectifs (vente, location, confort) et pourrez comparer les stratégies d'investissement."
                }
              },
              {
                "@type": "Question",
                "name": "Les aides financières sont-elles intégrées dans les calculs ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pas encore, mais c'est prévu pour le deuxième trimestre 2026. Actuellement, DPEWizard vous indique le coût brut des travaux et leur ROI immobilier (gain de valeur du bien). Les aides (MaPrimeRénov', CEE, éco-PTZ, etc.) ne sont pas déduites automatiquement car elles dépendent de nombreux critères (revenus, zone géographique, année de construction, cumul possible, etc.). En attendant l'intégration, nous vous conseillons de consulter Simul'Aides sur France-Renov.gouv.fr pour estimer vos aides personnalisées."
                }
              },
              {
                "@type": "Question",
                "name": "Combien puis-je vraiment économiser avec DPEWizard ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sur une vente immobilière, un DPE incorrect ou mal optimisé peut vous coûter entre 15 000€ et 40 000€ selon les études de notaires. En identifiant les erreurs de votre DPE et en simulant l'impact précis de chaque type de travaux, DPEWizard vous permet soit de récupérer cette décote, soit d'éviter des travaux inutiles qui n'amélioreront pas votre classe énergétique. Pour 9,90€, le retour sur investissement moyen constaté est de 2500%, que ce soit via une meilleure négociation de vente ou une priorisation intelligente de vos rénovations."
                }
              },
              {
                "@type": "Question",
                "name": "L'investissement vaut-il vraiment le coup ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Un seul devis de travaux inutiles vous coûtera plus cher que DPEWizard. Pour moins de 10€, vous obtenez une cartographie complète de tous les leviers d'amélioration de votre bien, avec leur impact chiffré sur votre DPE et leur rentabilité. C'est l'équivalent d'une consultation avec un bureau d'études thermiques, mais instantanée, interactive et 100 fois moins chère. Si votre DPE comporte des erreurs, l'économie sur la vente se chiffre en milliers d'euros. Si vous planifiez des travaux, vous évitez les mauvais choix qui coûtent des dizaines de milliers d'euros."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen">
        <Hero />
        <ProblemSection />
        <HowItWorks />
        <SolutionInAction />
        <Testimonials />
        <Pricing />
        <FAQ />
        <CTA />
      </div>
    </>
  );
};

export default HomePage;